/** Meetings as calendar events, for the dashboard's month and the Calendar
 * page: when each one starts and ends, and which platform it's on. */

import { useQuery } from "@tanstack/react-query";

import { endpoints, type Meeting } from "./api";

export type PlatformKey = "meet" | "teams" | "zoom";

export interface CalendarEvent {
  key: string;
  title: string;
  start: Date;
  /** The calendar's end time, or start + recorded length, or half an hour. */
  end: Date;
  platform: PlatformKey | null;
  url: string | null;
  /** Set once a bot was sent: links to the meeting's page. */
  sessionId: string | null;
  state: string;
  label: string;
  upcoming: boolean;
}

export const PLATFORM_NAMES: Record<PlatformKey, string> = {
  meet: "Google Meet",
  teams: "Microsoft Teams",
  zoom: "Zoom",
};

/** A meeting's app: from its link, or — for an upload, which has no link —
 * what the uploader said or the frames showed. */
export function meetingPlatformKey(meeting: { meet_url: string | null; platform?: string | null }): PlatformKey | null {
  const stored = meeting.platform;
  return platformKey(meeting.meet_url)
    ?? (stored === "meet" || stored === "teams" || stored === "zoom" ? stored : null);
}

/** "Google Meet", "In person", "Uploaded" (an upload whose app couldn't be told), or null. */
export function platformLabel(meeting: { meet_url: string | null; platform?: string | null; in_person?: boolean;
                                        uploaded?: boolean }): string | null {
  if (meeting.in_person) return "In person";
  const key = meetingPlatformKey(meeting);
  if (key) return PLATFORM_NAMES[key];
  return meeting.uploaded ? "Uploaded" : null;
}

export function platformKey(url: string | null): PlatformKey | null {
  if (!url) return null;
  if (url.includes("meet.google.com")) return "meet";
  if (url.includes("teams.microsoft.com") || url.includes("teams.live.com")) return "teams";
  if (url.includes("zoom.")) return "zoom";
  return null;
}

const HALF_HOUR = 30 * 60_000;

function toEvent(meeting: Meeting, upcoming: boolean): CalendarEvent | null {
  const when = meeting.starts_at ?? meeting.requested_at;
  if (!when) return null;
  const start = new Date(when);
  let end: Date;
  if (meeting.ends_at) end = new Date(meeting.ends_at);
  else if (meeting.duration_seconds) end = new Date(start.getTime() + meeting.duration_seconds * 1000);
  else end = new Date(start.getTime() + HALF_HOUR);
  return {
    key: meeting.session_id ?? `${meeting.meet_url}-${when}`,
    title: meeting.title ?? "Untitled meeting",
    start,
    end,
    platform: platformKey(meeting.meet_url),
    url: meeting.meet_url,
    sessionId: meeting.session_id,
    state: meeting.state,
    label: meeting.label,
    upcoming,
  };
}

/** Every meeting you can see, past and coming up, earliest first. */
export function useCalendarEvents() {
  const query = useQuery({ queryKey: ["recordings"], queryFn: endpoints.recordings, refetchInterval: 60_000 });
  const events: CalendarEvent[] = [];
  for (const meeting of query.data?.past ?? []) {
    const event = toEvent(meeting, false);
    if (event) events.push(event);
  }
  for (const meeting of query.data?.upcoming ?? []) {
    const event = toEvent(meeting, true);
    if (event) events.push(event);
  }
  events.sort((a, b) => a.start.getTime() - b.start.getTime());
  return { events, isLoading: query.isLoading, error: query.error as Error | null };
}

// --- dates -------------------------------------------------------------------

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Monday of the week `date` is in. */
export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() + 6) % 7));
}

/** The six Monday-first weeks that show `month` (any date in it). */
export function monthGrid(month: Date): Date[] {
  const first = startOfWeek(new Date(month.getFullYear(), month.getMonth(), 1));
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

export function eventsOn(events: CalendarEvent[], day: Date): CalendarEvent[] {
  return events.filter((event) => sameDay(event.start, day));
}

export function timeOf(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
